import { AdvancedSearchData } from '@/components/search/types';
import { searchCriteriaForEenheden, searchCriteriaForIds } from '@/components/search/v1/utils/helpers';
import { WerklijnInline } from '@/types/werklijnenInline';
import { getPlankToMwdDragItems, PlankToMwdDragItem, PlankToMwdDragPayload } from '@/utils/plankToMwdDrag';

/** What MWD should do once the werklijnen for a Plank drop have been loaded.
 *
 * - `none`: nothing resolved.
 * - `single`: one werklijn; selected and opened in the detail panel.
 * - `batch`: multiple werklijnen; selection is replaced and no detail panel opens, since there
 *   is no single "primary" werklijn to focus.
 */
export type PlankDropPlan =
  | { kind: 'none' }
  | { kind: 'single'; werklijnId: string; werklijnElementId?: string }
  | { kind: 'batch'; werklijnIds: string[] };

/** The items carrying a werklijnId or eenheidNummer. Others are skipped, so a partially
 * resolvable batch still opens whatever it can. */
export function getResolvablePlankDropItems(payload: PlankToMwdDragPayload): PlankToMwdDragItem[] {
  return getPlankToMwdDragItems(payload).filter((item) => item.werklijnId || item.eenheidNummer);
}

/** Groups items by lookup field, so a batch costs at most two requests (one by werklijn.id,
 * one by eenheid.nummer) instead of one per dragged row. */
export function toPlankDropSearchRequests(items: PlankToMwdDragItem[]): AdvancedSearchData[] {
  const werklijnIds = [...new Set(items.map((item) => item.werklijnId).filter((id): id is string => Boolean(id)))];
  const eenheidNummers = [
    ...new Set(
      items
        .filter((item) => !item.werklijnId)
        .map((item) => item.eenheidNummer)
        .filter((nummer): nummer is string => Boolean(nummer)),
    ),
  ];

  const requests: AdvancedSearchData[] = [];
  if (werklijnIds.length > 0) requests.push(searchCriteriaForIds(werklijnIds));
  if (eenheidNummers.length > 0) requests.push(searchCriteriaForEenheden(eenheidNummers));
  return requests;
}

/** Each item resolves either directly (it carried a werklijnId) or via the first search result
 * matching its eenheidNummer. Ids are deduped in drag order, so two rows pointing at the same
 * werklijn select it once. For a single resolved werklijn, the item's own werklijnElementId (if
 * any) is carried along so MWD can open the exact day/inzet that was dragged instead of
 * defaulting to the werklijn's first one. */
export function toPlankDropPlan(items: PlankToMwdDragItem[], werklijnen: WerklijnInline[]): PlankDropPlan {
  const resolved = items
    .map((item) => ({
      werklijnId: item.werklijnId ?? werklijnen.find((werklijn) => werklijn.eenheid.nummer === item.eenheidNummer)?.id,
      werklijnElementId: item.werklijnElementId,
    }))
    .filter((entry): entry is { werklijnId: string; werklijnElementId: string | undefined } =>
      Boolean(entry.werklijnId),
    );
  const werklijnIds = [...new Set(resolved.map((entry) => entry.werklijnId))];

  if (werklijnIds.length === 0) return { kind: 'none' };
  if (werklijnIds.length === 1) {
    const werklijnElementId = resolved.find((entry) => entry.werklijnId === werklijnIds[0])?.werklijnElementId;
    return werklijnElementId
      ? { kind: 'single', werklijnId: werklijnIds[0], werklijnElementId }
      : { kind: 'single', werklijnId: werklijnIds[0] };
  }
  return { kind: 'batch', werklijnIds };
}
